import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { SeverityChip } from '@/components/ui/severity-chip';

/**
 * Issue severity chip: the one `<SeverityChip>` primitive (C2-55), kept
 * under this name for the issues route. The word is the stored severity
 * (Low / Medium / High / Critical), coloured by the severity ramp.
 */

export interface IssueSeverityChipProps {
  severity: string;
  className?: string;
}

export function IssueSeverityChip({ severity, className }: IssueSeverityChipProps) {
  return <SeverityChip severity={severity} className={className} />;
}

/**
 * Issue status chip (Open / In progress / Resolved). Same intent as the
 * severity chip — keeps issue-register rows in the posture vocabulary so
 * the screen reads consistently with the rest of the product.
 */
const STATUS_TO_POSTURE: Record<string, { status: PostureStatus; label: string }> = {
  open: { status: 'critical', label: 'Open' },
  in_progress: { status: 'at-risk', label: 'In progress' },
  resolved: { status: 'healthy', label: 'Resolved' },
  closed: { status: 'healthy', label: 'Closed' },
};

export function IssueStatusChip({ status, className }: { status: string; className?: string }) {
  const key = status.toLowerCase().replace(/\s+/g, '_');
  const mapped = STATUS_TO_POSTURE[key];
  if (!mapped) {
    return (
      <PostureChip status="unassessed" className={className}>
        {status}
      </PostureChip>
    );
  }
  return (
    <PostureChip status={mapped.status} className={className}>
      {mapped.label}
    </PostureChip>
  );
}
